import { useState, useCallback, useEffect } from 'react';
import {
  InputGroup,
  InputLeftElement,
  NumberInput,
  NumberInputField,
  type NumberInputProps,
} from '@chakra-ui/react';

interface CurrencyInputProps extends Omit<
  NumberInputProps,
  'value' | 'onChange'
> {
  value: number | null | undefined | '';
  onChange: (valueString: string, valueNumber: number | null) => void;
  showCurrencySymbol?: boolean;
  placeholder?: string;
  height?: number | string;
}

const formatWithThousands = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  return value.toLocaleString('en-CA');
};

const parseNumber = (value: string): number | null => {
  if (!value) return null;
  const cleaned = value.replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
};

const CurrencyInput = ({
  value,
  onChange,
  showCurrencySymbol = false,
  placeholder = '',
  height = 30,
  size = 'sm',
  variant = 'main',
  min = 0,
  isDisabled,
  ...rest
}: CurrencyInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (!isFocused) {
      const numValue = value === '' ? null : value;
      setInputValue(formatWithThousands(numValue));
    }
  }, [value, isFocused]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    const numValue = value === '' ? null : value;
    setInputValue(numValue?.toString() ?? '');
  }, [value]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    const numValue = parseNumber(inputValue);
    setInputValue(formatWithThousands(numValue));
  }, [inputValue]);

  const handleChange = useCallback(
    (valueString: string) => {
      setInputValue(valueString);
      const numValue = parseNumber(valueString);
      onChange(valueString, numValue);
    },
    [onChange],
  );

  const hasValue = inputValue !== '';

  return (
    <InputGroup size={size}>
      {hasValue && (
        <InputLeftElement
          height={height}
          pointerEvents="none"
          color="gray.700"
          fontSize="sm"
        >
          $
        </InputLeftElement>
      )}
      <NumberInput
        variant={variant}
        size={size}
        min={min}
        value={inputValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isDisabled={isDisabled}
        {...rest}
      >
        <NumberInputField
          height={height}
          placeholder={placeholder}
          pr={2}
          pl={hasValue ? 5 : undefined}
        />
      </NumberInput>
    </InputGroup>
  );
};

export default CurrencyInput;
